<template>
  <div class="mortgage-refinance flex-col gap-md">
    <dd-loading-indicator v-if="!calculationValues" />

    <!-- Overzicht van huidige data (alleen tonen als calculationValues beschikbaar is) -->
    <a-card v-if="calculationValues">
      <div class="flex-col gap-md">
        <h2>Uitgangspunten</h2>

        <!-- Hoofdklant -->
        <div v-if="calculationValues.applicant">
          <h3>Hoofdklant</h3>
          <dd-card-stacked-list
            :list="{
              ['Geboortedatum']: toDate(
                calculationValues.applicant.date_of_birth
              ),
              ['Inkomen uit werk']: toCurrency(
                calculationValues.applicant.annual_employment_income,
                0
              ),
              ['Inkomen uit AOW']: toCurrency(
                calculationValues.applicant.old_age_pension_income,
                0
              ),
              ['Inkomen uit werkgeverspensioen']: toCurrency(
                calculationValues.applicant.state_pension_income,
                0
              ),
            }"
          />
        </div>
        <div v-if="calculationValues.partner">
          <h3>Partner gegevens</h3>
          <dd-card-stacked-list
            :list="{
              ['Geboortedatum']: toDate(
                calculationValues.partner.date_of_birth
              ),
              ['Inkomen uit werk']: toCurrency(
                calculationValues.partner.annual_employment_income,
                0
              ),
              ['Inkomen uit AOW']: toCurrency(
                calculationValues.partner.old_age_pension_income,
                0
              ),
              ['Inkomen uit werkgeverspensioen']: toCurrency(
                calculationValues.partner.state_pension_income,
                0
              ),
            }"
          />
        </div>
        <div>
          <h3>Woning gegevens:</h3>
          <dd-card-stacked-list
            :list="{
              ['WOZ waarde']: toCurrency(calculationValues.property_tax_value),
              ['Marktwaarde']: toCurrency(calculationValues.market_value),
            }"
          />
        </div>
        <div>
          <h3>Geldverstrekker:</h3>
          <img
            :src="calculationValues.current_mortgage.image_url"
            alt="Logo"
            style="max-height: 24px; margin-right: 8px"
          />
          <dd-card-stacked-list
            :list="{
              ['Productlijn']:
                calculationValues.current_mortgage.product_line_name ||
                'Onbekend',
            }"
          />
        </div>

        <div>
          <!-- Leningsdelen -->
          <h3 class="mb-2" style="margin-top: 24px">Leningsdelen</h3>
          <p v-if="customerId">
            In deze berekenen betrekken we alle hypotheken die gekoppeld zijn
            aan het huidige woonadres.
          </p>
          <dd-main-table
            rounded
            :rows="calculationValues.loan_parts"
            :headers="loanPartColumns"
            :has-select="false"
          >
          </dd-main-table>
        </div>

        <a-divider type="horizontal" />

        <dd-select
          v-model="newInterestPeriodInMonths"
          title="Nieuwe rentevaste periode (jaren)"
        >
          <a-select-option
            v-for="period in possibleInterestPeriods"
            :key="period"
            :value="period"
          >
            {{ period / 12 }}
          </a-select-option>
        </dd-select>

        <dd-switch
          title="Oversluitvergoeding meefinancieren"
          v-model="financeRepaymentPenalty"
        ></dd-switch>

        <div>
          <dd-button
            type="primary"
            @click="calculateRepayment"
            :loading="calcLoading"
          >
            Bereken
          </dd-button>
          <div class="mt-xs" v-if="repaymentResult">
            <small>
              Laatst berekend op {{ repaymentResult.calculatedAt | toDate }}
            </small>
          </div>
        </div>
      </div>
    </a-card>

    <a-card v-if="repaymentResult">
      <h3>Huidige maandlast</h3>
      <dd-card-stacked-list
        :list="{
          ['Bruto maandlast']: toCurrency(
            repaymentResult.currentMortgageSituation.grossMonthlyPayments
          ),
          ['Netto maandlast']: toCurrency(
            repaymentResult.currentMortgageSituation.netMonthlyPayments
          ),
        }"
      />
    </a-card>

    <!-- Gebruikersinvoer + Berekenknop -->
    <a-card v-if="calculationValues" class="mb-3">
      <div v-if="isRememberedResult">
        <p>
          Dit is een opgeslagen resultaat. De oversluit voorstellen komen
          misschien niet meer overeen met de huidige hypotheek.
        </p>
      </div>

      <!-- Resultaten van de herberekening -->
      <a-spin :spinning="calcLoading" tip="Bezig met berekenen...">
        <div v-if="repaymentResult">
          <h3>Oversluit voorstellen</h3>
          <!-- Tabel met nieuwe aanbiedingen -->
          <a-table
            :dataSource="getRefinanceProposals"
            :columns="proposalColumns"
            :pagination="false"
          >
            <span slot="product" slot-scope="text, proposal">
              <img
                :src="proposal.imageUrl"
                alt="Logo"
                style="max-height: 24px; margin-right: 8px"
              />
              <br />
              <p>{{ proposal.productLineName }}</p>
            </span>
            <span
              slot="interestRateLargestMortgagePart"
              slot-scope="text, proposal"
            >
              {{
                (proposal.interestRateLargestMortgagePart * 100)
                  | toPercentage(2)
              }}
            </span>
            <span slot="grossMonthlyPayments" slot-scope="text">
              {{ text | toCurrency(0) }}
            </span>
            <span slot="grossDifferencePerMonth" slot-scope="text, proposal">
              {{ proposal.grossDifferencePerMonth | toCurrency(0) }}
            </span>
            <span
              slot="grossTotalRefinanceDifference"
              slot-scope="text, proposal"
            >
              {{ text | toCurrency(0) }}
            </span>
            <span slot="action" slot-scope="text, record">
              <a-button type="link" @click="openModal(record)">
                Details
              </a-button>
            </span>
          </a-table>
        </div>
        <p v-else>
          Geen resultaten, druk op 'Bereken' om de berekening uit te voeren.
        </p>
      </a-spin>

      <!-- Foutmelding -->
      <div v-if="errorMessage" class="mt-3">
        <p style="color: red">{{ errorMessage }}</p>
      </div>
    </a-card>

    <!-- De modal voor extra details van één geselecteerde oversluitoptie -->
    <a-modal
      v-model="isModalVisible"
      title="Details oversluitoptie"
      @ok="closeModal"
      @cancel="closeModal"
      width="1000px"
    >
      <div v-if="selectedProposal" ref="printSection" class="flex-col gap-md">
        <div class="flex-row justify-content-between">
          <!-- Toon hier alle fields, bijvoorbeeld in een lijstje of een <pre> blok -->

          <h3>
            Oversluit voorstel van
            {{ selectedProposal.productLineName }}
          </h3>
          <img
            :src="selectedProposal.imageUrl"
            alt="Logo"
            style="max-height: 40px; margin-right: 8px"
          />
        </div>

        <p v-if="selectedProposal.fitsOnIncome">
          Dit voorstel past op het inkomen.
        </p>
        <p v-else>Let op: Dit voorstel past niet op het inkomen.</p>

        <table style="width: 100%">
          <thead class="ant-table-thead">
            <tr>
              <th></th>
              <th>Leningsdeel</th>
              <th>RVP</th>
              <th>Rente</th>
              <th>Bruto</th>
              <th>Netto</th>
            </tr>
          </thead>
          <tbody class="ant-table-tbody">
            <tr class="ant-table-row">
              <td colspan="4">Huidige maandlast</td>
              <td>
                {{
                  repaymentResult.currentMortgageSituation.grossMonthlyPayments
                    | toCurrency(0)
                }}
              </td>
              <td>
                {{
                  repaymentResult.currentMortgageSituation.netMonthlyPayments
                    | toCurrency(0)
                }}
              </td>
            </tr>
            <tr v-for="part in calculationValues.loan_parts" :key="part.id">
              <td></td>
              <td>
                {{
                  repaymentTypeTranslation[part.repayment_type] ||
                  part.repayment_type
                }}
              </td>
              <td>{{ part.remaining_interest_period_in_months }}</td>
              <td>{{ part.interest_rate | toPercentage(2) }}</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td colspan="4">
                Nieuwe maandlast bij {{ selectedProposal.productLineName }}
              </td>
              <td>
                {{ selectedProposal.grossMonthlyPayments | toCurrency(0) }}
              </td>
              <td>
                {{ selectedProposal.netMonthlyPayments | toCurrency(0) }}
              </td>
            </tr>
            <tr
              v-for="(rate, i) in selectedProposal.interestRatesPerMortgagePart"
              :key="i"
            >
              <td></td>
              <td>
                {{
                  calculationValues.loan_parts[i]
                    ? repaymentTypeTranslation[
                        calculationValues.loan_parts[i].repayment_type
                      ] || calculationValues.loan_parts[i].repayment_type
                    : "Onbekend"
                }}
              </td>
              <td>{{ newInterestPeriodInMonths }}</td>
              <td>{{ (rate * 100) | toPercentage(2) }}</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>Verschil per maand</td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                {{ selectedProposal.grossDifferencePerMonth | toCurrency(0) }}
              </td>
              <td>
                {{ selectedProposal.netDifferencePerMonth | toCurrency(0) }}
              </td>
            </tr>
          </tbody>
          <h3>Totaal</h3>
          <tbody class="ant-table-tbody mt-4">
            <tr>
              <td colspan="4">
                Verschil in maandlast over {{ newInterestPeriodInMonths }} maand
              </td>
              <td>
                {{
                  selectedProposal.grossTotalDifferenceDuringInterestPeriod
                    | toCurrency(0)
                }}
              </td>
              <td></td>
            </tr>
            <tr>
              <td colspan="4">Terugverdientijd</td>
              <td>
                {{
                  selectedProposal.paybackTimeInMonths
                    ? selectedProposal.paybackTimeInMonths
                    : "n.v.t."
                }}
              </td>
              <td></td>
            </tr>
            <tr>
              <td colspan="4">Vergoeding</td>
              <td>
                {{ repaymentResult.repaymentPenalty | toCurrency(0) }}
              </td>
              <td></td>
            </tr>
            <tr>
              <td colspan="4">Advieskosten</td>
              <td>
                {{
                  repaymentResult.refinanceCosts.mortgageBrokerageCosts
                    | toCurrency(0)
                }}
              </td>
              <td></td>
            </tr>
            <tr>
              <td colspan="4">Notariskosten</td>
              <td>
                {{
                  repaymentResult.refinanceCosts.appraisalCosts | toCurrency(0)
                }}
              </td>
              <td></td>
            </tr>
            <tr>
              <td colspan="4">Taxatiekosten</td>
              <td>
                {{
                  repaymentResult.refinanceCosts.notarialMortgageActCosts
                    | toCurrency(0)
                }}
              </td>
              <td></td>
            </tr>
            <tr>
              <td colspan="6"></td>
            </tr>
            <tr>
              <td colspan="4">
                <h3>
                  Totaal verschil over {{ newInterestPeriodInMonths }} maand
                </h3>
              </td>
              <td>
                {{
                  selectedProposal.grossTotalRefinanceDifference | toCurrency(0)
                }}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else>
        <p>Geen informatie beschikbaar.</p>
      </div>

      <a-button @click="printResult">Print resultaat</a-button>
    </a-modal>
  </div>
</template>

<script>
import compareApi from "~/api/compare.js";
import { toCurrency, toDate, toPercentage } from "~/plugins/string-helpers";

export default {
  name: "MortgageRefinance",
  props: {
    mortgageId: [String, Number],
    customerId: [String, Number],
  },
  data() {
    return {
      repaymentTypeTranslation: {
        annuity: "Annuiteit",
        linear: "Lineair",
        life_insurance: "Levensverzekering",
        interest_only: "Aflossingsvrij",
        investment_mortgage: "Beleggingshypotheek",
        savings: "Spaarhypotheek",
        bank_savings: "Bankspaar",
      },
      // Gegevens
      calculationValues: null,
      repaymentResult: null,

      // Input velden
      newInterestPeriodInMonths: 120,
      financeRepaymentPenalty: false,
      possibleInterestPeriods: [12, 60, 120, 180, 240, 360],

      // UI-state
      errorMessage: null,
      calcLoading: false, // Om te tracken of we bezig zijn met berekenen
      isModalVisible: false, // Of de detail-modal open staat
      selectedProposal: null, // Bevat de data van de aangeklikte proposal
      isRememberedResult: false,
    };
  },

  created() {
    this.fetchCalculationValues();
  },

  methods: {
    toDate,
    toCurrency,
    printResult() {
      const printContent = this.$refs.printSection.innerHTML;
      const originalContent = document.body.innerHTML;
      document.body.innerHTML = printContent;
      window.print();
      document.body.innerHTML = originalContent;
      window.location.reload(); // Herlaad de pagina om de originele inhoud te herstellen
    },
    async fetchCalculationValues() {
      try {
        let response = await compareApi.getCalculationValues("mortgage", {
          mortgage_id: this.mortgageId,
          customer_id: this.customerId,
        });
        this.calculationValues = response.calculationValues;
        if (response.lastSavedResult) {
          this.repaymentResult = response.lastSavedResult;
          this.isRememberedResult = true;
        }
      } catch (error) {
        this.errorMessage =
          "Er is een fout opgetreden bij het ophalen van de waarden.";
        console.error(error);
      }
    },

    async calculateRepayment() {
      this.calcLoading = true;
      this.errorMessage = null;
      try {
        const params = {
          new_interest_period_in_months: this.newInterestPeriodInMonths,
          finance_repayment_penalty: this.financeRepaymentPenalty,
          mortgage_id: this.mortgageId,
          customer_id: this.customerId,
        };
        let response = await compareApi.save("mortgage", params);
        // Refresh the result
        this.repaymentResult = null;
        this.repaymentResult = response.data;
        this.isRememberedResult = false;
      } catch (error) {
        this.errorMessage =
          "Er is een fout opgetreden bij het berekenen van het nieuwe aanbod.";
        console.error(error);
      } finally {
        this.calcLoading = false;
      }
    },

    // Voor de modals
    openModal(proposal) {
      this.selectedProposal = proposal;
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
      this.selectedProposal = null;
    },
  },
  // Tabeldefinities (AntD)
  computed: {
    loanPartColumns() {
      return [
        {
          title: "Type aflossing",
          width: "30px",
          key: (part) => {
            return (
              this.repaymentTypeTranslation[part.repayment_type] || "Onbekend"
            );
          },
        },
        {
          title: "Huidig bedrag",
          width: "30px",
          key: (part) => {
            return toCurrency(part.current_amount, 0);
          },
        },
        {
          title: "Rente (%)",
          width: "30px",
          key: (part) => {
            return toPercentage(part.interest_rate, 2);
          },
        },
        {
          title: "Looptijd (mnd)",
          width: "30px",
          key: "duration_in_months",
        },
        {
          title: "RVP (mnd)",
          width: "30px",
          key: "remaining_interest_period_in_months",
        },
        {
          title: "Aftrekbare periode (mnd)",
          width: "30px",
          key: "deductible_period_in_months",
        },
      ];
    },

    proposalColumns() {
      return [
        {
          title: "Product",
          key: "product",
          scopedSlots: { customRender: "product" },
        },
        {
          title: "Rente percentage",
          key: "interestRateLargestMortgagePart",
          scopedSlots: { customRender: "interestRateLargestMortgagePart" },
        },
        {
          title: "Nieuwe maandlast",
          dataIndex: "grossMonthlyPayments",
          key: "grossMonthlyPayments",
          scopedSlots: { customRender: "grossMonthlyPayments" },
        },
        {
          title: "Bespaar per maand",
          key: "grossDifferencePerMonth",
          scopedSlots: { customRender: "grossDifferencePerMonth" },
        },
        {
          title: "Bespaar in totaal",
          dataIndex: "grossTotalRefinanceDifference",
          key: "grossTotalRefinanceDifference",
          scopedSlots: { customRender: "grossTotalRefinanceDifference" },
        },
        {
          title: "Actie",
          key: "action",
          align: "center",
          scopedSlots: { customRender: "action" },
        },
      ];
    },

    getRefinanceProposals() {
      if (
        !this.repaymentResult ||
        !this.repaymentResult.refinanceProposalPerProduct
      ) {
        return [];
      }
      return this.repaymentResult.refinanceProposalPerProduct.sort((a, b) => {
        // Check if either item is the "preferred" product line
        if (a.productLineId === this.calculationValues.lender_hdn_code)
          return -1;
        if (b.productLineId === this.calculationValues.lender_hdn_code)
          return 1;

        // Items with 'fitsOnIncome: false' go at bottom
        if (!a.fitsOnIncome && b.fitsOnIncome) return 1;
        if (a.fitsOnIncome && !b.fitsOnIncome) return -1;

        // Otherwise, sort descending by 'grossTotalRefinanceDifference'
        return (
          b.grossTotalRefinanceDifference - a.grossTotalRefinanceDifference
        );
      });
    },
  },
};
</script>

<style scoped>
.mb-2 {
  margin-bottom: 16px;
}

.mb-3 {
  margin-bottom: 24px;
}

.mt-3 {
  margin-top: 24px;
}
</style>
